import { createSelector } from 'reselect';

import { SelectedItemMap } from '~/core/models';
import { RootState } from '~/redux/store';

export const selectSelectedOptionalItemId = ({ optionalItem }: RootState) =>
  optionalItem.selectedOptionalItemId;

export const selectSelectedOptionalItems = ({ optionalItem }: RootState) =>
  optionalItem.selectedOptionalItems;

export const selectPickedCustomLaborOptionalItems = ({
  optionalItem,
}: RootState) => optionalItem.selectedCustomLaborOptionalItems;

export const selectSelectedWarrantyProductId = ({ optionalItem }: RootState) =>
  optionalItem.selectedWarrantyProductId;

/** To support legacy structure */
export const selectSelectedOptionalItemsAsSelectedItemMap = createSelector(
  [
    ({ optionalItem }: RootState) => optionalItem.selectedOptionalItems,
    ({ optionalItem }: RootState) => optionalItem.selectedWarrantyProductId,
    ({ optionalItem }: RootState) =>
      optionalItem.selectedCustomLaborOptionalItems,
  ],
  (
    selectedOptionalItems,
    selectedWarrantyProductId,
    selectedCustomLaborOptionalItems
  ): SelectedItemMap => {
    // Making the map from every optionalItem that is not a warranty plan or a custom labor item
    const selectedItemMap = Object.entries(selectedOptionalItems).reduce(
      (acc, [id, quantity]) => ({
        ...acc,
        [id]: {
          quantity,
          isSelected: true,
          isManuallyChanged: true,
        },
      }),
      {} as SelectedItemMap
    );

    // Adding warranty product to the map
    if (selectedWarrantyProductId) {
      selectedItemMap[selectedWarrantyProductId] = {
        quantity: 1,
        isSelected: true,
        isManuallyChanged: true,
      };
    }

    // making a map from custom labor items only
    const mapFromCustomLabor = Object.values(selectedCustomLaborOptionalItems)
      .flat()
      .reduce<SelectedItemMap>((acc, { productId, quantity, notes }) => {
        if (!acc[productId]) {
          acc[productId] = {
            quantity: 0,
            isSelected: true,
            isManuallyChanged: true,
            detailEntries: [],
          };
        }

        acc[productId].quantity += quantity;
        acc[productId].note = appendNote(acc[productId].note, trimNote(notes));
        acc[productId].detailEntries?.push({ quantity, note: notes });

        return acc;
      }, {} as SelectedItemMap);

    // Putting both the map with optional items and warranty product, and the map with custom labor together
    return { ...selectedItemMap, ...mapFromCustomLabor };
  }
);

const appendNote = (
  existingNote: string | undefined,
  newNote: string
): string => (existingNote ? `${existingNote}\n${newNote}` : newNote);

const trimNote = (note: string) => note.replace(/\n+/g, '\n').trim();
